import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography, Grid, TextField, Button, Alert } from '@mui/material';
import AppLayout from '../components/AppLayout';
import api from '../api/client';
import { useAuth } from '../contexts/AuthContext';

function isDigit(ch) {
  return /^[0-9]$/.test(ch);
}

// Input de 1 dígito (numérico para C* e D*)
const DigitBox = React.forwardRef(({ value, onChange, onKeyDown, onPaste, disabled = false }, ref) => (
  <TextField
    value={value}
    inputRef={ref}
    onChange={onChange}
    onKeyDown={onKeyDown}
    onPaste={onPaste}
    type="text"
    size="small"
    disabled={disabled}
    inputProps={{
      maxLength: 1,
      inputMode: 'numeric',
      pattern: '[0-9]',
      style: { textAlign: 'center', fontWeight: 600 }
    }}
    sx={{ width: 50 }}
  />
));

// decodifica o sub (id) do token do localStorage
function getIdFromToken() {
  try {
    const t = localStorage.getItem('token');
    if (!t) return null;
    const payload = JSON.parse(atob(t.split('.')[1] || ''));
    return payload?.sub ? String(payload.sub) : null;
  } catch { return null; }
}

export default function ContraSenha() {
  const { user } = useAuth();

  const [C, setC] = useState(['', '', '', '']);                 // C1..C4 (0-9)
  const [D, setD] = useState(['', '', '', '', '', '']);         // D1..D6 (0-9)
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lockC, setLockC] = useState(false);                    // trava os C quando vier preenchido

  const cRefs = useRef(Array.from({ length: 4 }, () => React.createRef()));
  const dRefs = useRef(Array.from({ length: 6 }, () => React.createRef()));

  // helper: aplica o código nos 4 campos C* e trava
  const prefillFromOwnerCode = (cp) => {
    if (!cp) return;
    const onlyDigits = String(cp).replace(/\D+/g, '').slice(0, 4);
    const filled = [
      onlyDigits[0] || '',
      onlyDigits[1] || '',
      onlyDigits[2] || '',
      onlyDigits[3] || '',
    ];
    setC(filled);
    setLockC(true);
  };

  // PREFILL do Código Proprietário (de user ou buscando /api/senhas/:id)
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        // 1) tentou vir no contexto?
        let cp = user?.cod_proprietario ? String(user.cod_proprietario) : '';

        // 2) não veio? busca pelo id (sub) do JWT ou id do contexto
        if (!cp) {
          const id = user?.id_senha || user?.id || user?.sub || getIdFromToken();
          if (id) {
            const { data } = await api.get(`/api/api/senhas/${id}`);
            cp = data?.cod_proprietario ? String(data.cod_proprietario) : '';
          }
        }

        if (!active || !cp) return;
        prefillFromOwnerCode(cp);
      } catch {
        // se falhar, deixa vazio mesmo
      }
    })();
    return () => { active = false; };
  }, [user]);

  const focusNext = (group, index) => {
    if (group === 'C') {
      // se os C estão travados, pula direto pro primeiro D
      if (lockC) { dRefs.current[0].current?.focus(); return; }
      if (index < 3) cRefs.current[index + 1].current?.focus();
      else dRefs.current[0].current?.focus();
    } else {
      if (index < 5) dRefs.current[index + 1].current?.focus();
    }
  };
  const focusPrev = (group, index) => {
    if (group === 'C') {
      if (lockC) return; // travado, não navega para trás nos C
      if (index > 0) cRefs.current[index - 1].current?.focus();
    } else {
      if (index > 0) dRefs.current[index - 1].current?.focus();
      else if (!lockC) cRefs.current[3].current?.focus();
    }
  };

  const handleChange = (group, i) => (e) => {
    if (group === 'C' && lockC) return; // travado
    const raw = e.target.value.slice(-1);
    const ok = isDigit(raw);            // agora C e D são numéricos
    if (!raw) {
      (group === 'C' ? setC : setD)(arr => arr.map((v, idx) => (idx === i ? '' : v)));
      return;
    }
    if (!ok) return;
    (group === 'C' ? setC : setD)(arr => arr.map((v, idx) => (idx === i ? raw : v)));
    focusNext(group, i);
  };

  const handleKeyDown = (group, i) => (e) => {
    if (group === 'C' && lockC) { e.preventDefault(); return; }
    if (e.key === 'Backspace' && (group === 'C' ? C[i] : D[i]) === '') {
      e.preventDefault();
      focusPrev(group, i);
      (group === 'C' ? setC : setD)(arr => arr.map((v, idx) => (idx === Math.max(0, i - 1) ? '' : v)));
    }
    if (e.key === 'ArrowLeft') { e.preventDefault(); focusPrev(group, i); }
    if (e.key === 'ArrowRight') { e.preventDefault(); focusNext(group, i); }
  };

  const handlePaste = (group, i) => (e) => {
    if (group === 'C' && lockC) { e.preventDefault(); return; }
    const text = (e.clipboardData || window.clipboardData).getData('text');
    if (!text) return;
    e.preventDefault();
    const chars = text.split('').filter(isDigit);
    if (!chars.length) return;

    if (group === 'C') {
      setC(prev => {
        const arr = [...prev];
        let idx = i;
        for (const ch of chars) {
          if (idx > 3) break;
          arr[idx++] = ch;
        }
        return arr;
      });
      const nextIdx = Math.min(3, i + chars.length);
      if (nextIdx <= 3) cRefs.current[nextIdx].current?.focus();
      else dRefs.current[0].current?.focus();
    } else {
      setD(prev => {
        const arr = [...prev];
        let idx = i;
        for (const ch of chars) {
          if (idx > 5) break;
          arr[idx++] = ch;
        }
        return arr;
      });
      const nextIdx = Math.min(5, i + chars.length);
      dRefs.current[nextIdx]?.current?.focus();
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const body = {
        // agora C também vai como inteiro decimal (0–9)
        C1: parseInt(C[0] || '0', 10),
        C2: parseInt(C[1] || '0', 10),
        C3: parseInt(C[2] || '0', 10),
        C4: parseInt(C[3] || '0', 10),
        D1: parseInt(D[0] || '0', 10),
        D2: parseInt(D[1] || '0', 10),
        D3: parseInt(D[2] || '0', 10),
        D4: parseInt(D[3] || '0', 10),
        D5: parseInt(D[4] || '0', 10),
        D6: parseInt(D[5] || '0', 10),
      };
      const { data } = await api.post('/api/api/gerar', body);
      setResult(data);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Erro ao gerar contra-senha';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    // mantém C travados se vieram do cadastro; limpa só D e resultado
    setD(['','','','','','']);
    setResult(null);
  };

  return (
    <AppLayout>
      <Typography variant="h5" fontWeight={700} gutterBottom>Senha Descartável</Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3, borderStyle: 'dashed', textAlign: 'center' }}>
        <Typography variant="h3" letterSpacing={8}>{result?.contra_senha || '______'}</Typography>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={1} sx={{ p: 3 }} component="form" onSubmit={submit}>
        <Typography variant="subtitle1" gutterBottom>Código Proprietário</Typography>
        <Grid container spacing={1} alignItems="center" sx={{ mb: 2 }}>
          {C.map((v, i) => (
            <Grid item key={`C${i}`}>
              <DigitBox
                value={v}
                ref={cRefs.current[i]}
                disabled={lockC}
                onChange={handleChange('C', i)}
                onKeyDown={handleKeyDown('C', i)}
                onPaste={handlePaste('C', i)}
              />
            </Grid>
          ))}
        </Grid>

        <Typography variant="subtitle1" gutterBottom>Número Gerado</Typography>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          {D.map((v, i) => (
            <Grid item key={`D${i}`}>
              <DigitBox
                value={v}
                ref={dRefs.current[i]}
                onChange={handleChange('D', i)}
                onKeyDown={handleKeyDown('D', i)}
                onPaste={handlePaste('D', i)}
              />
            </Grid>
          ))}
        </Grid>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? 'Gerando...' : 'Enviar'}
          </Button>
          <Button variant="outlined" onClick={reset}>Limpar</Button>
        </Box>
      </Paper>

      {result && (
        <Alert severity="success" sx={{ mt: 2 }}>
          contagem de solicitações: <b>{result.decode_count}</b>
        </Alert>
      )}
    </AppLayout>
  );
}
